import { useRef, useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useOrgAuth } from '../contexts/OrgAuthContext'
import { MODULE_CONFIGS } from '../config/modules'
import { Z } from '../lib/zIndex'
import { useDialogBackHandler } from '../hooks/useDialogBackHandler'
import { ChevronDown, LayoutGrid, X, Lock } from 'lucide-react'
import Badge from './ui/Badge'

export default function NavLauncher() {
  const { t } = useTranslation()
  const { session } = useOrgAuth()
  const location = useLocation()
  const [open, setOpen] = useState(false)
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 })
  const ref = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)

  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 639px)').matches,
  )

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return
    const mq = window.matchMedia('(max-width: 639px)')
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])

  // Close on outside click (desktop)
  useEffect(() => {
    if (!open || isMobile) return
    function handleOutside(e: MouseEvent) {
      const target = e.target as Node
      if (
        ref.current && !ref.current.contains(target) &&
        panelRef.current && !panelRef.current.contains(target)
      ) setOpen(false)
    }
    document.addEventListener('mousedown', handleOutside)
    return () => document.removeEventListener('mousedown', handleOutside)
  }, [open, isMobile])

  // Close on Escape
  useEffect(() => {
    if (!open) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  // Back / swipe-back → close (mirrors NotificationPanel; the mobile sheet is a
  // full-screen take-over, so the gesture should dismiss it).
  useDialogBackHandler(open, () => setOpen(false))

  // Prevent scroll on mobile when open
  useEffect(() => {
    if (open && isMobile) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [open, isMobile])

  // Close on navigation (unconditional – avoids stale-closure edge cases)
  useEffect(() => {
    setOpen(false)
  }, [location.pathname])

  // Focus management: move focus into the panel on open and return it to the
  // trigger on close. The `wasOpenRef` guard avoids stealing focus on first mount.
  const wasOpenRef = useRef(false)
  useEffect(() => {
    if (open) {
      panelRef.current?.focus()
      wasOpenRef.current = true
    } else if (wasOpenRef.current) {
      triggerRef.current?.focus()
      wasOpenRef.current = false
    }
  }, [open])

  const activeModulesSet = new Set(session?.current_organization?.active_modules ?? [])
  const userPermissions = new Set(session?.permissions ?? [])

  const activeModuleKey = MODULE_CONFIGS.find(
    (m) => !m.disabled && m.path !== '/app' && location.pathname.startsWith(m.path),
  )?.key ?? null

  // Pre-calculate state and sort: active modules first, then inactive ones
  const sortedModules = [...MODULE_CONFIGS].map(mod => {
    const isActivated = mod.isAlwaysActive || activeModulesSet.has(mod.key)
    const hasPermission = !mod.requiredPermission || userPermissions.has(mod.requiredPermission)
    const isActive = isActivated && !mod.disabled && hasPermission
    const isCurrent = activeModuleKey === mod.key
    return { ...mod, isActivated, hasPermission, isActive, isCurrent }
  }).sort((a, b) => {
    if (a.isActive === b.isActive) return 0
    return a.isActive ? -1 : 1
  })

  return (
    <div className="relative flex items-center" ref={ref}>
      {/* Trigger button */}
      <button
        ref={triggerRef}
        type="button"
        onClick={() => {
          if (!open && ref.current) {
            const r = ref.current.getBoundingClientRect()
            setDropdownPos({ top: r.bottom + 8, left: r.left })
          }
          setOpen((v) => !v)
        }}
        className={`flex items-center gap-2 text-sm font-semibold tracking-wide text-slate-700 select-none hover:opacity-80 transition-opacity p-2 -ms-2 rounded-md ${
          open ? 'bg-slate-100' : ''
        }`}
        title={t('common.modules', 'Modules')}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <img src="/logo.svg" alt="Logo" className="w-6 h-6 flex-shrink-0" />
        {/* Short wordmark: the header also carries module actions that need the room */}
        <span>Rochd</span>
        <ChevronDown
          size={14}
          aria-hidden="true"
          className={`text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        isMobile ? (
          /* ── Mobile: Full screen slide drawer ── */
          <div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-label={t('common.modules', 'Apps')}
            tabIndex={-1}
            className={`fixed inset-0 z-[${Z.modal}] flex flex-col overflow-hidden bg-slate-50 outline-none`}
          >
            {/* Safe area spacer for status bar */}
            <div className="pt-safe bg-white" />

            {/* Mobile Header */}
            <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-slate-200 shrink-0 shadow-sm z-10">
              <span className="text-base font-semibold text-slate-800">
                {t('common.modules', 'Apps')}
              </span>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label={t('common.close', 'Close')}
                className="p-2 -me-2 rounded-full text-slate-500 hover:bg-slate-100 hover:text-slate-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Mobile Grid */}
            <div className="flex-1 overflow-y-auto overscroll-contain px-4 py-8 pb-safe">
              <div className="grid grid-cols-3 gap-y-10 gap-x-4">
                {sortedModules.map((mod) => {
                  const cell = (
                    <>
                      <div
                        className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-transform ${
                          mod.isActive
                            ? `active:scale-90 border border-slate-200/60 ${mod.colors.iconText}`
                            : 'bg-slate-200 text-slate-400'
                        }`}
                      >
                        {mod.icon}
                      </div>
                      <span className="text-sm font-medium text-slate-700 text-center leading-tight">
                        {t(mod.titleKey, mod.titleFallback)}
                      </span>

                      {mod.isActive &&
                        mod.tags?.map((tag) => (
                          <Badge
                            key={tag.labelKey}
                            tone={tag.tone}
                            className="absolute -top-1 start-1 px-1.5 py-0 text-[9px]"
                          >
                            {t(tag.labelKey, tag.labelFallback)}
                          </Badge>
                        ))}

                      {(!mod.isActivated || !mod.hasPermission) && !mod.disabled && (
                        <div className="absolute -top-1 end-2 bg-white p-1 rounded-full shadow-sm border border-slate-200">
                          <Lock className="w-3 h-3 text-slate-500" />
                        </div>
                      )}
                    </>
                  )
                  return mod.isActive ? (
                    <Link
                      key={mod.key}
                      to={mod.path}
                      onClick={() => setOpen(false)}
                      aria-current={mod.isCurrent ? 'page' : undefined}
                      className="flex flex-col items-center gap-3 group relative"
                    >
                      {cell}
                    </Link>
                  ) : (
                    <div
                      key={mod.key}
                      aria-disabled="true"
                      className="flex flex-col items-center gap-3 group relative opacity-50 cursor-not-allowed grayscale-[0.8]"
                    >
                      {cell}
                    </div>
                  )
                })}
              </div>
            </div>

            {/* Mobile Footer */}
            <div className="bg-slate-50 border-t border-slate-200 p-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
              <Link
                to="/app"
                onClick={() => setOpen(false)}
                className="w-full px-4 py-3 text-sm font-semibold text-slate-700 hover:text-slate-900 bg-white border border-slate-200 rounded-xl shadow-sm transition-colors flex items-center gap-2 justify-center"
              >
                <LayoutGrid className="w-5 h-5" />
                {t('common.backToDashboard', 'All Apps')}
              </Link>
            </div>
          </div>
        ) : createPortal(
          /* ── Desktop: Office365-style Waffle Dropdown Popover ── */
          <div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-label={t('common.modules', 'Apps')}
            tabIndex={-1}
            className={`fixed flex flex-col rounded-xl border border-slate-200 bg-white shadow-xl z-[${Z.modal}] w-[320px] overflow-hidden outline-none`}
            style={{ top: dropdownPos.top, left: dropdownPos.left }}
          >
            {/* Header */}
            <div className="px-4 py-3 flex items-center justify-between">
              <span className="text-sm font-semibold text-slate-800">
                {t('common.modules', 'Apps')}
              </span>
            </div>

            {/* App Grid */}
            <div className="px-3 pb-3 grid grid-cols-3 gap-1">
              {sortedModules.map((mod) => {
                const cell = (
                  <>
                    <div
                      className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${
                        mod.isActive ? mod.colors.iconText : 'bg-slate-100 text-slate-400'
                      }`}
                    >
                      {mod.icon}
                    </div>
                    <span className="text-2xs font-medium text-slate-700 text-center leading-tight line-clamp-2 px-1">
                      {t(mod.titleKey, mod.titleFallback)}
                    </span>
                    {mod.isActive &&
                      mod.tags?.map((tag) => (
                        <Badge
                          key={tag.labelKey}
                          tone={tag.tone}
                          className="absolute top-1 start-1 px-1.5 py-0 text-[9px]"
                        >
                          {t(tag.labelKey, tag.labelFallback)}
                        </Badge>
                      ))}
                    {(!mod.isActivated || !mod.hasPermission) && !mod.disabled && (
                      <div className="absolute top-1 right-1 bg-white p-0.5 rounded-full shadow-sm border border-slate-200">
                        <Lock className="w-2.5 h-2.5 text-slate-500" />
                      </div>
                    )}
                  </>
                )
                const baseClass = 'relative flex flex-col items-center justify-start gap-2 p-3 rounded-xl'
                return mod.isActive ? (
                  <Link
                    key={mod.key}
                    to={mod.path}
                    onClick={() => setOpen(false)}
                    title={t(mod.titleKey, mod.titleFallback)}
                    aria-current={mod.isCurrent ? 'page' : undefined}
                    className={`${baseClass} transition-all hover:bg-slate-50 cursor-pointer`}
                  >
                    {cell}
                  </Link>
                ) : (
                  <div
                    key={mod.key}
                    aria-disabled="true"
                    className={`${baseClass} opacity-40 grayscale-[0.8] cursor-not-allowed`}
                  >
                    {cell}
                  </div>
                )
              })}
            </div>

            {/* Footer */}
            <div className="bg-slate-50 border-t border-slate-100 p-2">
              <Link
                to="/app"
                onClick={() => setOpen(false)}
                className="w-full px-3 py-2 text-xs font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-200/50 rounded-lg transition-colors flex items-center gap-2 justify-center"
              >
                <LayoutGrid className="w-4 h-4" />
                {t('common.backToDashboard', 'All Apps')}
              </Link>
            </div>
          </div>,
          document.body
        )
      )}
    </div>
  )
}
